<template>
  <div></div>
</template>

<script setup lang="ts">
import type {
  Map,
  GeoJSONSourceSpecification,
  GeoJSONSource,
  LineLayerSpecification
} from 'maplibre-gl'

const props = withDefaults(
  defineProps<{
    coordinates: [number, number][]
    map: Map
    cursor?: string
    lineColor?: string
    lineWidth?: number
    lineDasharray?: number[]
    borderLineColor?: string
  }>(),
  {
    cursor: 'pointer',
    lineWidth: 3,
    lineColor: '#9B9B9B',
    borderLineColor: '#fff'
  }
)

const emit = defineEmits([
  'mouseenter',
  'mousemove',
  'mouseleave',
  'mousedown',
  'click',
  'dblclick',
  'drag'
])

const state = reactive<{
  source: GeoJSONSource | null
  mapStyleSheetId: string | null
}>({
  source: null,
  mapStyleSheetId: null
})

const layerID = computed(() => {
  const id = Math.round(Math.random() * 1000000)

  return `route${id}`
})

const attemptRemoveLayers = () => {
  if (props.map.getLayer(layerID.value + '-cursor')) {
    props.map.removeLayer(layerID.value + '-cursor')
  }

  if (props.map.getLayer(layerID.value + '-border')) {
    props.map.removeLayer(layerID.value + '-border')
  }

  if (props.map.getLayer(layerID.value)) {
    props.map.removeLayer(layerID.value)
  }

  if (props.map.getSource(layerID.value)) {
    props.map.removeSource(layerID.value)
  }
}

const getFeatureFromRoute = () => {
  const feature: GeoJSON.Feature<GeoJSON.LineString> = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: props.coordinates
    },
    properties: {}
  }

  return feature
}

const createSource = () => {
  const source: GeoJSONSourceSpecification = {
    type: 'geojson',
    data: getFeatureFromRoute()
  }

  props.map.addSource(layerID.value, source)

  const updatedSource = props.map.getSource<GeoJSONSource>(layerID.value)

  if (updatedSource) {
    // @ts-expect-error - not sure why this causes a "type instantiation is excessively deep" error
    state.source = updatedSource
  }
}

const bindEvents = () => {
  props.map.on('mouseenter', layerID.value + '-cursor', (event) => {
    props.map.getCanvas().style.cursor = props.cursor
    emit('mouseenter', event)
  })

  props.map.on('mousemove', layerID.value + '-cursor', (event) => {
    props.map.getCanvas().style.cursor = props.cursor
    emit('mousemove', event)
  })

  props.map.on('mouseleave', layerID.value + '-cursor', () => {
    props.map.getCanvas().style.cursor = ''
    emit('mouseleave', event)
  })

  props.map.on('mousedown', layerID.value + '-cursor', (event) => {
    emit('mousedown', event)
  })

  props.map.on('click', layerID.value + '-cursor', (event) => {
    emit('click', event)
  })

  props.map.on('dblclick', layerID.value + '-cursor', (event) => {
    emit('dblclick', event)
  })

  // @ts-expect-error - not sure why drag is not recognized here
  props.map.on('drag', layerID.value + '-cursor', (event) => {
    emit('drag', event)
  })
}

const addLayer = () => {
  const layers = props.map.getStyle().layers

  const firstSymbolLayer = layers.find((layer) => layer.type === 'symbol')

  const referenceLayer = layers.find((layer) => layer.id === 'reference-layer')

  // layer just for adding extra padding around the route to make hovering less finicky
  const cursorLayer: LineLayerSpecification = {
    id: layerID.value + '-cursor',
    type: 'line',
    source: layerID.value,
    paint: {
      'line-width': props.lineWidth + 15,
      'line-color': 'transparent'
    }
  }

  props.map.addLayer(cursorLayer, referenceLayer?.id ?? firstSymbolLayer?.id)

  const borderLayer: LineLayerSpecification = {
    id: layerID.value + '-border',
    type: 'line',
    source: layerID.value,
    paint: {
      'line-width': props.lineWidth + 3,
      'line-color': props.borderLineColor
    }
  }

  props.map.addLayer(borderLayer, referenceLayer?.id ?? firstSymbolLayer?.id)

  const paint: LineLayerSpecification['paint'] = {
    'line-width': props.lineWidth,
    'line-color': props.lineColor
  }

  if (props.lineDasharray) {
    paint['line-dasharray'] = props.lineDasharray
  }

  const layer: LineLayerSpecification = {
    id: layerID.value,
    type: 'line',
    source: layerID.value,
    paint,
    layout: {
      'line-cap': 'round'
    }
  }
  props.map.addLayer(layer, referenceLayer?.id ?? firstSymbolLayer?.id)
}

const updateRoute = () => {
  state.source?.setData(getFeatureFromRoute())
}

const reDrawRoute = () => {
  attemptRemoveLayers()
  createSource()
  addLayer()
}

const styleDataChangeHandler = (newMap: Map) => {
  // @ts-expect-error - id is not typed
  if (state.mapStyleSheetId === newMap.style.stylesheet.id) {
    return
  }

  props.map.once('idle', reDrawRoute)

  // @ts-expect-error - id is not typed
  state.mapStyleSheetId = newMap.style.stylesheet.id
}

onMounted(() => {
  reDrawRoute()
  bindEvents()

  // @ts-expect-error - id is not typed
  state.mapStyleSheetId = props.map.style.stylesheet.id

  props.map.on('styledata', styleDataChangeHandler)
})

onBeforeUnmount(() => {
  props.map.off('styledata', styleDataChangeHandler)

  attemptRemoveLayers()
})

watch(
  () => props.coordinates,
  () => {
    updateRoute()
  },
  {
    deep: true
  }
)
</script>
